import { createSlice } from "@reduxjs/toolkit"
import types from "../../Actions/types"
import { ADDITIONAL_COURSE_ID } from "../../../Constants/courses"

const moduleInById = (modulesById, moduleId) => {
  if (!modulesById || moduleId == null || moduleId === -1) return false
  return !!(modulesById[moduleId] || modulesById[String(moduleId)])
}

const initialState = {
  initialized: false,
  initializedPart: false,
  fetchingModules: false,
  fetchingParts: false,
  fetchingElements: false,
  fetchingPartData: false,
  partIsLoading: false,
  savingPYP: false,
  incrementingPYP: false,
  initializingPYPInputs: false,
  currentPartComplete: true,
  courses: {},
  modules: {},
  parts: {
    allIds: [],
    byId: {}
  },
  partData: {},
  elements: {},
  navigation: {
    moduleNavigationMetaData: {},
    currentCourseId: -1,
    currentCourseName: "",
    currentModuleId: -1,
    currentPartId: -1,
    showIncompletionError: false
  },
  unlockedUserModules: null
}

const pypSlice = createSlice({
  name: "pyp",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.PART_IS_LOADING, (state, action) => {
      state.partIsLoading = action.data.partIsLoading
    })
    builder.addCase(types.INCREMENTING_PYP, (state) => {
      state.incrementingPYP = true
    })
    builder.addCase(types.INCREMENTED_PYP, (state) => {
      state.incrementingPYP = false
    })
    builder.addCase(types.SAVING_PYP, (state) => {
      state.savingPYP = true
    })
    builder.addCase(types.SAVED_PYP, (state) => {
      state.savingPYP = false
    })
    builder.addCase(types.EXIT_MODULE, (state) => {
      state.navigation.currentPartId = -1
      state.initializedPart = false
    })
    builder.addCase(types.DECREMENT_PART, (state, action) => {
      const moduleNavigationObj = {}
      moduleNavigationObj[action.data.moduleId] = {
        id: action.data.moduleId,
        lastVisitedPart: action.data.partId
      }
      state.navigation.currentPartId = action.data.partId || -1
      state.navigation.currentModuleId = action.data.moduleId
      state.navigation.moduleNavigationMetaData = Object.assign(
        state.navigation.moduleNavigationMetaData || {},
        moduleNavigationObj
      )
      state.parts = {
        allIds: state.parts?.allIds || [],
        byId: Object.assign(
          state.parts?.byId || {},
          action.data.updatedPart || {}
        )
      }
      state.unlockedUserModules = action.data.unlockedUserModules
        ? action.data.unlockedUserModules
        : state.unlockedUserModules
      state.initializedPart = !!action.data.partId
    })
    builder.addCase(types.INCREMENT_PART, (state, action) => {
      const moduleNavigation = {}
      moduleNavigation[action.data.moduleId] = {
        id: action.data.moduleId,
        lastVisitedPart: action.data.partId
      }
      state.navigation.currentPartId = action.data.partId || -1
      state.navigation.currentModuleId = action.data.moduleId
      state.navigation.moduleNavigationMetaData = Object.assign(
        state.navigation.moduleNavigationMetaData || {},
        moduleNavigation
      )
      state.unlockedUserModules = action.data.unlockedUserModules
      state.initializedPart = !!action.data.partId
      state.incrementingPYP = false
      
      // Recalculate completion status if partData is already available
      // This fixes the issue where Next button is disabled even when fields are filled
      if (action.data.partId && state.partData?.byId) {
        const currentPart = state.parts?.byId?.[action.data.partId]
        if (currentPart && currentPart.elements && currentPart.elements.length > 0) {
          const partDataId = currentPart.partData
          const partData = state.partData.byId[partDataId]?.data
          const elements = currentPart.elements.map(
            (elementId) => state.elements?.byId?.[elementId]
          ).filter(Boolean)
          
          if (partData && elements.length > 0) {
            // Recalculate completion using the same logic as fetchPartData
            // We'll import partIsComplete dynamically or use a helper
            // For now, set to false and let FETCHED_PART_DATA recalculate it properly
            // But trigger a fetch if data exists to ensure completion is recalculated
            state.currentPartComplete = false
            // The fetchPartData action will be called and will recalculate it
          } else {
            state.currentPartComplete = false
          }
        } else {
          state.currentPartComplete = false
        }
      } else {
        state.currentPartComplete = false
      }
    })
    builder.addCase(types.FETCHED_PART_DATA, (state, action) => {
      state.partData = action.data.partData
      state.parts = {
        allIds: state.parts?.allIds || [],
        byId: Object.assign(
          state.parts?.byId || {},
          action.data.updatedPart || {}
        )
      }
      state.currentPartComplete = action.data.currentPartComplete
      state.fetchingPartData = false
    })
    builder.addCase(types.FETCHING_PART_DATA, (state) => {
      state.fetchingPartData = true
    })
    builder.addCase(types.INITIALIZED_PART, (state, action) => {
      state.parts = action.data.parts
      state.elements = action.data.elements
      state.partData = action.data.partData
      state.initializedPart = true
      state.navigation.currentPartId = action.data.navigation.currentPartId
      if (action.data.navigation?.moduleNavigationMetaData) {
        state.navigation.moduleNavigationMetaData = Object.assign(
          state.navigation.moduleNavigationMetaData || {},
          action.data.navigation.moduleNavigationMetaData
        )
      }
      state.currentPartComplete = action.data.currentPartComplete
    })
    builder.addCase(types.FETCHED_PART_INPUTS, (state, action) => {
      state.partData = action.data.partData
      state.currentPartComplete = action.data.currentPartComplete
      state.parts = {
        allIds: state.parts?.allIds || [],
        byId: Object.assign(
          state.parts?.byId || {},
          action.data.updatedPart || {}
        )
      }
    })
    builder.addCase(types.INITIALIZING_PYP, (state) => {
      state.initialized = false
    })
    builder.addCase(types.INITIALIZING_PYP_INPUTS, (state) => {
      state.initializingPYPInputs = true
    })
    builder.addCase(types.FAILED_PYP_INPUTS_INIT, (state) => {
      state.initializingPYPInputs = false
    })
    builder.addCase(types.CREATE_PYP_INPUTS, (state, action) => {
      state.initializingPYPInputs = false
      if (typeof action.data.currentPartComplete === "boolean") {
        state.currentPartComplete = action.data.currentPartComplete
      }
      state.partData = action.data.partData
    })
    builder.addCase(types.SET_PYP_REFERENCE_INPUTS, (state, action) => {
      state.currentPartComplete = action.data.currentPartComplete
      state.partData = action.data.partData
    })
    builder.addCase(types.SET_PYP_BUTTON_SELECTION, (state, action) => {
      const currentPart = state.parts?.byId?.[state.navigation.currentPartId]
      if (!currentPart) return
      
      const currentPartDataId = currentPart.partData
      state.currentPartComplete = action.data.currentPartComplete
      state.partData = {
        ...state.partData,
        byId: {
          ...(state.partData?.byId || {}),
          [currentPartDataId]: {
            ...(state.partData?.byId?.[currentPartDataId] || {}),
            data: {
              ...(state.partData?.byId?.[currentPartDataId]?.data || {}),
              valuesToUpdate: action.data.buttonSelections?.value,
              selectedItems: action.data.buttonSelections?.selectedItems
            }
          }
        }
      }
    })
    builder.addCase(types.INSERT_CUSTOM_BUTTON, (state, action) => {
      const currentPart = state.parts?.byId?.[state.navigation.currentPartId]
      if (!currentPart) return
      
      const partDataId = currentPart.partData
      const currentPartData = state.partData?.byId?.[partDataId]
      if (!currentPartData) return
      
      const currentButtonValues = currentPartData.data?.buttonValues || []
      state.partData = {
        ...state.partData,
        byId: {
          ...(state.partData?.byId || {}),
          [partDataId]: {
            ...currentPartData,
            data: {
              ...(currentPartData.data || {}),
              buttonValues: [action.data.newButton, ...currentButtonValues]
            }
          }
        }
      }
    })
    builder.addCase(types.SET_PYP_INPUT_DATA, (state, action) => {
      state.currentPartComplete = action.data.currentPartComplete
      state.partData = action.data.partData
      console.log('[PYP Reducer] SET_PYP_INPUT_DATA - currentPartComplete updated to:', action.data.currentPartComplete)
    })
    builder.addCase(types.UPDATE_PART_INCOMPLETION_ERROR, (state, action) => {
      state.navigation.showIncompletionError = action.data.showIncompletionError
    })
    builder.addCase(types.FETCHING_PARTS, (state) => {
      state.fetchingParts = true
    })
    builder.addCase(types.NAVIGATE_TO_MODULE, (state, action) => {
      state.navigation.currentModuleId = action.data.moduleId
      state.navigation.currentPartId = -1
      state.initializedPart = false
    })
    builder.addCase(types.BEGIN_MODULE, (state, action) => {
      state.unlockedUserModules = action.data.unlockedUserModules
    })
    builder.addCase(types.INITIALIZED_PYP, (state, action) => {
      state.modules = action.data.modules
      state.courses = action.data.course
      const modulesById = action.data.modules?.byId || {}
      const incomingModuleId = action.data.currentModuleId
      state.navigation.currentModuleId =
        incomingModuleId != null &&
        incomingModuleId !== -1 &&
        moduleInById(modulesById, incomingModuleId)
          ? incomingModuleId
          : -1
      state.navigation.currentCourseId = action.data.currentCourseId ?? -1
      state.navigation.currentCourseName = action.data.currentCourseName ?? ""
      state.navigation.moduleNavigationMetaData =
        action.data.moduleNavigationMetaData
      state.unlockedUserModules = action.data.unlockedUserModules
      state.initialized = true
      state.fetchingModules = false
    })
    // addCase handlers must come before any addMatcher handlers (RTK v2 rule)
    builder.addCase(types.FETCHED_MODULES, (state, action) => {
      const incoming = action.data
      const modulesById = incoming?.byId
      // retrieveAllPYPModules error path dispatches []. Do not wipe the workshop.
      if (!modulesById || Array.isArray(incoming)) {
        return
      }
      // PYP home fetch must not replace an active eMpowered course.
      if (Number(state.navigation.currentCourseId) === ADDITIONAL_COURSE_ID) {
        return
      }
      state.modules = incoming
      const currentModuleId = state.navigation.currentModuleId
      if (!moduleInById(modulesById, currentModuleId)) {
        state.navigation.currentModuleId = -1
      }
    })
    builder.addCase(types.LOGOUT, (state) => {
      state.initialized = false
      state.fetchingModules = false
      state.fetchingSteps = false
      state.fetchingElements = false
      state.courses = {}
      state.modules = {}
      state.parts = {
        allIds: [],
        byId: {}
      }
      state.elements = {}
      state.navigation = {
        moduleNavigationMetaData: {},
        currentCourseId: -1,
        currentCourseName: "",
        currentModuleId: -1,
        currentPartId: -1,
        showIncompletionError: false
      }
      state.unlockedUserModules = null
    })

    // Matchers must be registered after all addCase handlers
    builder.addMatcher(
      (action) =>
        !!action && typeof action.type === "string" && action.type === types.FETCHING_MODULES,
      (state) => {
        state.fetchingModules = true
      }
    )
  }
})

export default pypSlice.reducer
